import { validateCoupons } from 'src/util/coupon';
import { checkQuizStatus } from 'src/services/quiz-service';
import paymentAPI from 'src/pages/deprecated/payment/api';
import {
  filterStipeErrorMessage,
  getEcommerceDataProperties,
} from 'src/lib/utils';
import { EVENTS, track, trackSignupPaymentCompleted } from 'src/util/track';
import {
  getAllowedCampaignCode,
  getCampaignFromSession,
} from 'src/util/campaign.util';
import history from 'src/lib/history';
import { SubscriptionStartData, SubscriptionStartResult } from '../types';

export const startSubscription = async (
  props: SubscriptionStartData
): Promise<SubscriptionStartResult> => {
  const {
    user,
    paymentMethod,
    priceId,
    coupon,
    isChallengeAccepted = false,
    futureCommencement = false,
    productId,
    challengeObject,
    walletPaymentIntentId = '',
    noIntentAvailable = false,
    isPersonalCoachingAdded = false,
    personalCoachPriceId = '',
    showFreeTrial = false,
    isChallengePackageFlow = false,
    signupAddonPriceIds = [],
    claimGift = false,
    selectedPlanId = '',
    allPlansSorted = [],
    effectivePlanObj,
    totalPrice = '0',
    campaignCode,
  } = props;

  const errorMsg = validateCoupons(priceId, coupon?.metadata);
  if (errorMsg) {
    return { errorMsg };
  }

  let quizFinalStatus = false;

  // checking quiz status before final payment
  const quizStatusRes = await checkQuizStatus({ email: user.email });
  if (quizStatusRes.status === 200 && quizStatusRes?.data?.data) {
    quizFinalStatus = true;
  }

  // Create Customer
  const { data } = await paymentAPI.post('stripe/customer/create', {
    email: user.email,
    userID: user.id,
  });

  // Store customerId for potential addon purchases
  const createdCustomerId = data.customer?.id;

  const priceIds = [priceId];
  if (isPersonalCoachingAdded && personalCoachPriceId) {
    priceIds.push(personalCoachPriceId);
  }

  const allowedCampaignCode = getAllowedCampaignCode(
    campaignCode || getCampaignFromSession()
  );

  const purchaseInvoicePayload: Record<string, any> = {
    paymentMethodID: paymentMethod.id,
    customerID: data.customer?.id,
    productId: productId,
    priceIDs: priceIds,
    promoID: coupon?.id || null,
    userID: user.id,
    isChallenge: !!isChallengeAccepted,
    isCardPayment: true,
    futureCommencement,
    isQuiz: quizFinalStatus,
    isFreeTrial: showFreeTrial,
    isChallengePackage: isChallengePackageFlow,
    viaSharedRecipe:
      (history?.location?.state as { viaSharedRecipe?: boolean })
        ?.viaSharedRecipe ?? false,
    addonPriceIds: signupAddonPriceIds,
    claimGift,
  };

  if (allowedCampaignCode) {
    purchaseInvoicePayload.campaignCode = allowedCampaignCode;
  }

  if (walletPaymentIntentId?.length > 0) {
    purchaseInvoicePayload.paymentIntentId = walletPaymentIntentId;
  }
  if (!!isChallengeAccepted && challengeObject?.id) {
    purchaseInvoicePayload.challenge_ids = [challengeObject.id];
  }

  // Process Payment
  if (noIntentAvailable || isChallengePackageFlow) {
    const purchaseResponse = await paymentAPI
      .post('stripe/invoice/purchase', purchaseInvoicePayload)
      .catch(error => {
        if (error.response) {
          return error.response.data;
        }
        throw error;
      });
    if (purchaseResponse?.message) {
      return { errorMsg: filterStipeErrorMessage(purchaseResponse.message) };
    }
  } else {
    const walletResponse = await paymentAPI
      .post('stripe/invoice/purchase/wallets', purchaseInvoicePayload)
      .catch(error => {
        if (error.response) {
          return error.response.data;
        }
        throw error;
      });
    if (walletResponse?.message) {
      return { errorMsg: filterStipeErrorMessage(walletResponse?.message) };
    }
  }

  const trackProperties =
    getEcommerceDataProperties(
      selectedPlanId || priceId,
      allPlansSorted,
      {
        payment_type: 'CREDIT_DEBIT_CARD',
        coupon_id: coupon?.id,
        coupon_name: coupon?.name,
        total_amount: Number(effectivePlanObj?.unitAmount || 0),
        per_cycle_amount: Number(effectivePlanObj?.perCycleAmount || 0),
        value: Number(effectivePlanObj?.unitAmount || 0),
      },
      1,
      user
    ) ?? {};

  track(EVENTS.addPaymentInfo, { ecommerce: null });
  track(EVENTS.addPaymentInfo, trackProperties);
  trackSignupPaymentCompleted(
    'card',
    selectedPlanId || priceId,
    Number(totalPrice)
  );

  return { customerId: createdCustomerId };
};
